import $http from '../../../../axios-instance';
import Swal from 'sweetalert2';

/**
 * Cargar todos los tanques de una empresa.
 *
 * @param {*} { commit }: store del módulo.
 * @param {*} { companyId, productId, page, perPage, companiesIds }: parámetros.
 * @returns {Promise<boolean>}
 * @author Fernando Claros
 */
export async function getTanksByCompanyId({ commit }, { companyId, productId, page, perPage, companiesIds }) {
  commit('resetDataFilter')
  commit('setLoadingDataTable', true)
  commit('setTableConfig', { items: [], data: { total: 0 } })

  await $http.get(`signaling-of-tanks/company/${companyId}/product/${productId}?companiesIds=${companiesIds}&page=${page}&perPage=${perPage}`)
    .then(({ data: response }) => {

      if (typeof response.data === 'object' && 'data' in response.data) {
        commit('setTableConfig', {
          items: response.data.data,
          data: response.data
        })
      }

    })
    .catch((error) => {
      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Señalización de tanques',
        text: 'Ups, ocurrió un error al tratar de cargar los tanques asignados al producto.',
        showConfirmButton: true,
      })
    })
    .finally(() => commit('setLoadingDataTable', false));
}

/**
 * Cargar detalles del tanque de una empresa.
 *
 * @param {*} { commit }: store del módulo.
 * @param {*} { companyId, tankId }: parámetros.
 * @returns {Promise<boolean>}
 * @author Fernando Claros
 */
export async function getTanksById({ commit }, { companyId, tankId }) {
  commit('dashboard/load', true, { root: true });
  await $http.get(`signaling-of-tanks/company/${companyId}/tank/${tankId}`)
    .then(({ data: response }) => {
      
      try {

        const tank = response.data;

        // Verificar si la localización esta inactiva.
        let location_id = tank.location_id;
        if (!tank.location.active) location_id = null;

        // Asignar el tanque
        commit('setSelectedTank', {
          "id": tank.id,
          "number_tank": tank.number_tank,
          "quantity": tank.quantity,
          "unit_of_measure": typeof tank.volume_unit === 'object' && 'lista' in tank.volume_unit ? tank.volume_unit.lista : '',
          "location_id": location_id,
          "volume_unit_id": tank.volume_unit.id,
          "is_active": tank.is_active,
          "prod_product_id": typeof tank.product_assign === 'object' && 'id_producto' in tank.product_assign ? tank.product_assign.id_producto : null
        })

        // Abrir el modal
        commit('setModal', { isOpen: true, action: 'EDIT', title: 'Editar tanques' });

      } catch (e) {
        throw new Error("Ups, ocurrió un error al tratar de procesar los detalles del tanque");
      }
      
    })
    .catch(() => {
      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Señalización de tanques',
        text: 'Ups, ocurrió un error al tratar de cargar los detalles del tanque.',
        showConfirmButton: true,
      })
    })
    .finally(() => commit('dashboard/load', false, { root: true }));
}

/**
 * Crear tanques.
 *
 * @param {*} { commit }: store del módulo.
 * @param {*} { companyId, payload }: parámetros.
 * @returns {Promise<boolean>}
 * @author Fernando Claros
 */
export async function store({ commit, dispatch }, { companyId, payload }) {
  commit('dashboard/load', true, { root: true });

  await $http.post(`signaling-of-tanks/company/${companyId}`, payload)
    .then(async ({ data: response }) => {

      // Recargar el histórico
      await dispatch('getTanksByCompanyId', {
        companyId,
        productId: payload.prod_product_id,
        page: 1,
        perPage: 10,
        companiesIds: []
      });

      Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Señalización de tanques',
        text: response.message,
        showConfirmButton: true,
      })

      // Limpiar y reiniciar los datos necesarios
      commit('resetModal');
      commit('resetSelectedTank');

    })
    .catch(errors => {

      // Errores de validaciones
      if (processCallBackErrors(errors)) return false;

      // Alerta del error
      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Señalización de tanques',
        text: 'Ups, ocurrió un error al tratar de crear el tanque.',
        showConfirmButton: true,
      })
    })
    .finally(() => commit('dashboard/load', false, { root: true }));
}

/**
 * Actualizar tanques.
 *
 * @param {*} { commit }: store del módulo.
 * @param {*} { companyId, payload }: parámetros.
 * @returns {Promise<boolean>}
 * @author Fernando Claros
 */
export async function update({ commit, dispatch }, { companyId, payload }) {
  commit('dashboard/load', true, { root: true });

  await $http.put(`signaling-of-tanks/company/${companyId}`, payload)
    .then(async ({ data: response }) => {

      // Recargar el histórico
      await dispatch('getTanksByCompanyId', {
        companyId,
        productId: payload.prod_product_id,
        page: 1,
        perPage: 10,
        companiesIds: []
      });

      Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Señalización de tanques',
        text: response.message,
        showConfirmButton: true,
      })

      // Limpiar y reiniciar los datos necesarios
      commit('resetModal');
      commit('resetSelectedTank');

    })
    .catch(errors => {

      // Errores de validaciones
      if (processCallBackErrors(errors)) return false;

      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Señalización de tanques',
        text: 'Ups, ocurrió un error al tratar de actualizar el tanque.',
        showConfirmButton: true,
      })
    })
    .finally(() => commit('dashboard/load', false, { root: true }));
}

/**
 * Obtener las listas con los datos de Unidad de volumen.
 *
 * @param {*} { commit }: store del módulo.
 * @returns {Promise<boolean>}
 * @author Fernando Claros
 */
export async function getUnitOfMeasures({ commit }) {
  commit('dashboard/load', true, { root: true });

  await $http.get("sys_list/unit-of-measures")
    .then(({ data: response }) => commit('setUnitOfMeasures', response.data))
    .catch(() => {
      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Señalización de tanques',
        text: 'Ups, ocurrió un error al tratar de cargar las Unidades de volumen.',
        showConfirmButton: true,
      })
    })
    .finally(() => commit('dashboard/load', false, { root: true }));
}

/**
 * Controlar los errores de validaciones del formulario..
 *
 * @param {object} errors
 * @returns {boolean}
 * @author Fernando Claros
 */
function processCallBackErrors(errors) {
  try {

    if ('data' in errors && typeof errors.data === 'object' && errors.status == 400) {

      let errorsText = "<ul><li style='list-style: none;'>Datos incorrectos.</li>";
      Object.values(errors.data).forEach(item => errorsText += `<li style='font-size: 16px;' class='text-left;'><i>${item}</i></li>`);
      errorsText += "</ul>";

      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Señalización de tanques',
        html: errorsText,
        showConfirmButton: true
      });

      return true;
    }

    return false;

  } catch (e) {
    return false;
  }
}